
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Brain, Settings, TrendingUp, AlertCircle, CheckCircle2 } from "lucide-react";
import { EnhancedColorResult, ColorFeedback } from "@/hooks/useEnhancedColorCheck";

interface AILearningPanelProps {
  colorResult: EnhancedColorResult | null;
  isLearning: boolean;
  onProvideFeedback: (feedback: ColorFeedback) => void;
}

const AILearningPanel = ({ 
  colorResult, 
  isLearning, 
  onProvideFeedback 
}: AILearningPanelProps) => {
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [correctionInput, setCorrectionInput] = useState("");
  const [notesInput, setNotesInput] = useState("");

  const handleDetailedFeedback = () => {
    if (!colorResult) return;

    onProvideFeedback({
      analysisId: colorResult.analysisId,
      feedbackType: 'incorrect',
      actualBaseColor: correctionInput,
      notes: notesInput
    });

    // Reset form
    setCorrectionInput("");
    setNotesInput("");
    setFeedbackDialogOpen(false);
  };

  if (!colorResult) {
    return (
      <Card className="border-blue-200 bg-blue-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center text-blue-800">
            <Brain className="mr-2 h-5 w-5" />
            AI Learning Systeem
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-blue-700">
            Het AI systeem staat klaar om te leren van kleuranalyses. Start een analyse om feedback te kunnen geven.
          </p>
        </CardContent>
      </Card>
    );
  }

  const confidenceLevel = colorResult.basePrediction.confidence;
  const needsFeedback = confidenceLevel < 0.8;

  return (
    <Card className={`border-2 ${needsFeedback ? 'border-orange-200 bg-orange-50/50' : 'border-green-200 bg-green-50/50'}`}>
      <CardHeader className="pb-3">
        <CardTitle className={`flex items-center justify-between ${needsFeedback ? 'text-orange-800' : 'text-green-800'}`}>
          <div className="flex items-center">
            <Brain className="mr-2 h-5 w-5" />
            AI Learning Systeem
            {isLearning && <div className="ml-2 animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />}
          </div>
          <Badge variant={needsFeedback ? "destructive" : "default"}>
            {needsFeedback ? "Feedback gewenst" : "Hoge zekerheid"}
          </Badge>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Confidence Meter */}
          <div className="text-center">
            <div className={`text-2xl font-bold ${confidenceLevel > 0.8 ? 'text-green-600' : confidenceLevel > 0.6 ? 'text-yellow-600' : 'text-red-600'}`}>
              {Math.round(confidenceLevel * 100)}%
            </div>
            <div className="text-sm text-muted-foreground">AI Zekerheid</div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
              <div 
                className={`h-2 rounded-full ${confidenceLevel > 0.8 ? 'bg-green-500' : confidenceLevel > 0.6 ? 'bg-yellow-500' : 'bg-red-500'}`}
                style={{ width: `${confidenceLevel * 100}%` }}
              />
            </div>
          </div>

          {/* Learning Data Points */}
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {colorResult.learningMetrics.historicalDataPoints}
            </div>
            <div className="text-sm text-muted-foreground">Geleerde patronen</div>
            <TrendingUp className="mx-auto h-4 w-4 mt-1 text-blue-500" />
          </div>

          {/* Feedback Status */}
          <div className="text-center">
            <div className={`text-2xl font-bold ${needsFeedback ? 'text-orange-600' : 'text-green-600'}`}>
              {needsFeedback ? <AlertCircle className="mx-auto h-8 w-8" /> : <CheckCircle2 className="mx-auto h-8 w-8" />}
            </div>
            <div className="text-sm text-muted-foreground">
              {needsFeedback ? "Feedback nodig" : "Goede analyse"}
            </div>
          </div>
        </div>

        {/* Feedback Actions */}
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            onClick={() => onProvideFeedback({
              analysisId: colorResult.analysisId,
              feedbackType: 'correct',
              notes: 'AI analyse bevestigd als correct'
            })}
            disabled={isLearning}
          >
            <CheckCircle2 className="mr-1 h-3 w-3" />
            Correct
          </Button>

          <Dialog open={feedbackDialogOpen} onOpenChange={setFeedbackDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="flex-1" disabled={isLearning}>
                <Settings className="mr-1 h-3 w-3" />
                Correctie
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>AI Correctie & Feedback</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Juiste basis kleur:</label>
                  <Input
                    placeholder="bijv. Wit, Medium, Donker, Transparant"
                    value={correctionInput}
                    onChange={(e) => setCorrectionInput(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Opmerkingen voor AI learning:</label>
                  <Textarea
                    placeholder="Leg uit waarom de analyse incorrect was..."
                    value={notesInput}
                    onChange={(e) => setNotesInput(e.target.value)}
                  />
                </div>
                <Button 
                  onClick={handleDetailedFeedback}
                  disabled={!correctionInput.trim() || isLearning}
                  className="w-full"
                >
                  {isLearning ? "Feedback verwerken..." : "Verstuur correctie"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Current Analysis Summary */}
        <div className="bg-white/70 p-3 rounded-lg">
          <div className="text-sm">
            <div className="font-medium">Huidige analyse:</div>
            <div className="text-muted-foreground">
              {colorResult.parsedInfo.colorCode || 'Onbekende kleur'} → 
              <span className="font-medium ml-1">{colorResult.basePrediction.baseColor} basis</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {colorResult.basePrediction.reasoning}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AILearningPanel;
